// ─── Image Normalizer for Image Search Upload ───
// 긴 변(max dimension): 1280px
// 작은 이미지는 확대하지 않음 (downscale only)
// format: WebP / quality: 0.85
// 원본 aspect ratio 유지
// EXIF orientation 보정 (createImageBitmap handles this in modern browsers)
// 권장 목표 파일크기: 약 100~500KB
// ──────────────────────────────────────────────────

const TARGET_MAX_DIMENSION = 1280;
const WEBP_QUALITY = 0.85;

export interface NormalizeResult {
  file: File; // normalized WebP file
  originalWidth: number;
  originalHeight: number;
  normalizedWidth: number;
  normalizedHeight: number;
}

/**
 * 고객이 업로드한 이미지를 이미지 검색에 최적화된 WebP로 정규화합니다.
 * - EXIF orientation 보정 (createImageBitmap 자동 처리)
 * - 긴 변 1280px로 리사이즈 (원본이 더 작으면 그대로 유지)
 * - WebP 변환 (quality 0.85)
 */
export async function normalizeImageForSearch(file: File): Promise<NormalizeResult> {
  let img: ImageBitmap | null = null;

  try {
    // createImageBitmap은 최신 브라우저에서 EXIF orientation을 자동 보정
    img = await createImageBitmap(file);
  } catch {
    throw new Error('이미지를 읽을 수 없습니다. 파일이 손상되었거나 지원하지 않는 형식입니다.');
  }

  const origW = img.width;
  const origH = img.height;

  // ─── 리사이즈 계산 (downscale only, aspect ratio 유지) ───
  const longEdge = Math.max(origW, origH);
  let targetW = origW;
  let targetH = origH;

  if (longEdge > TARGET_MAX_DIMENSION) {
    const scale = TARGET_MAX_DIMENSION / longEdge;
    targetW = Math.round(origW * scale);
    targetH = Math.round(origH * scale);
  }

  // ─── Canvas 렌더링 ───
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    img.close();
    throw new Error('이미지 처리를 위한 Canvas를 생성할 수 없습니다.');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetW, targetH);
  img.close();

  // ─── WebP 내보내기 ───
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b && b.size > 0) {
          resolve(b);
        } else {
          reject(new Error('WebP 변환에 실패했습니다.'));
        }
      },
      'image/webp',
      WEBP_QUALITY,
    );
  });

  // ─── Blob → File ───
  const baseName = file.name.replace(/\.[^.]+$/, '');
  const normalizedFile = new File([blob], `${baseName}.webp`, {
    type: 'image/webp',
    lastModified: Date.now(),
  });

  console.log('[image-normalizer] Normalization complete:', {
    original: `${origW}x${origH} (${(file.size / 1024).toFixed(1)}KB)`,
    normalized: `${targetW}x${targetH} (${(blob.size / 1024).toFixed(1)}KB)`,
    downscaled: longEdge > TARGET_MAX_DIMENSION,
  });

  return {
    file: normalizedFile,
    originalWidth: origW,
    originalHeight: origH,
    normalizedWidth: targetW,
    normalizedHeight: targetH,
  };
}